import { FC, useEffect, useState } from 'react';
import { SearchContext } from '../GlobalSearchPopup';
import BPPopup from 'components/BionicUI/popup/BPPopup';
import BPMarkdown from 'components/BionicUI/markdown/BPMarkdown';
import SearchResultCards from './SearchResultCards';
import Eye from 'components/onboarding/Eye';
import BPInstruction from 'components/BionicUI/instruction/BPInstruction';
import { Skeleton } from 'components/BionicUI/ui/skeleton';
import { useOpenAISearch } from 'components/search/result/useOpenAISearch';
import { Disclaimer } from './Disclaimer';

interface SearchResultProps {
  handleClose: () => void;
  searchContext?: SearchContext;
  searchTerm?: string;
}

const SearchResult: FC<SearchResultProps> = ({
  searchTerm,
  searchContext,
  handleClose,
}) => {
  const { result } = useOpenAISearch({ query: searchTerm });

  return (
    <>
      <BPPopup
        rainbowEdge
        cycleRainbow={!result}
        animateExpand
        handleClose={handleClose}
        padding="p-[30px]"
        fillScreenHeight
      >
        <div className="grid relative w-full gap-8">
          <TitleWithEye searchTerm={searchTerm} />
          <div className="grid relative w-full lg:grid-cols-[1fr_auto] gap-6">
            {!result ? (
              <ResponseSkeleton />
            ) : (
              <BPMarkdown markdown={result} className="animate-fade-in" />
            )}
            <SearchResultCards searchContext={searchContext} />
          </div>
        </div>
      </BPPopup>
      <Disclaimer />
    </>
  );
};

const TitleWithEye: FC<{ searchTerm?: string }> = ({ searchTerm = '' }) => {
  const [playTextAnim, setPlayTextAnim] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => setPlayTextAnim(true), 500);
  }, []);

  return (
    <div className="grid relative grid-cols-[auto_1fr] gap-4 items-center">
      <div className="block relative w-[80px] h-[80px]">
        <Eye showBorder />
      </div>
      <BPInstruction
        line1={{ text: 'Finding the answer to' }}
        line2={{ text: searchTerm }}
        play={playTextAnim}
        twTextClasses="gap-0"
        twLine1TextClasses="text-bionic-green text-[20px]"
        twLine2TextClasses="text-[24px]"
      />
    </div>
  );
};

const ResponseSkeleton: FC = () => (
  <>
    <div>
      <div className="grid relative w-full max-w-[600px] h-auto gap-5 opacity-40">
        <Skeleton className="w-[40%] h-[20px] rounded-[3px]" />
        <Skeleton className="w-[32%] h-[20px] rounded-[3px] mb-3" />
        <Skeleton className="w-[58%] h-[16px] rounded-[3px]" />
        <Skeleton className="w-[52%] h-[16px] rounded-[3px]" />
        <Skeleton className="w-[45%] h-[16px] rounded-[3px]" />
        <Skeleton className="w-[48%] h-[16px] rounded-[3px]" />
      </div>
    </div>
  </>
);

export default SearchResult;
